import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output,
  SimpleChanges, ViewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { ReplaySubject, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-lazyload-multiple-select',
  templateUrl: './lazyload-multiple-select.component.html',
  styleUrls: ['./lazyload-multiple-select.component.scss']
})
export class LazyLoadMultipleSelectComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  @Input() data: any;
  @Input() required: boolean;
  @Input() disabled: boolean;
  @Input() label: string;
  @Input() reset: boolean;
  @Input() resetValue: any[];
  @Input() defaultAll: boolean;
  @Input() values: any[] = [];

  @Output() result: EventEmitter<any[]> = new EventEmitter<any[]>();

  @ViewChild('allSelected') allSelected: MatOption;
  @ViewChild('multiple') multiple: MatSelect;
  @ViewChild(CdkVirtualScrollViewport) cdkVirtualScrollViewport: CdkVirtualScrollViewport;
  initialRange = { start: 0, end: 10 };

  matSelectSearchCtrl: FormControl = new FormControl();

  filteredData: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);

  protected onDestroy = new Subject<void>();
  public model: any[] = [];
  countdownTimeoutIndex: any;
  constructor(
    private cdr: ChangeDetectorRef
  ) {
    if (typeof(this.defaultAll) !== 'boolean') {
      this.defaultAll = true;
    }
  }

  ngOnInit() {
    // listen for search field value changes
    this.matSelectSearchCtrl.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        if (this.countdownTimeoutIndex) {
          clearTimeout(this.countdownTimeoutIndex);
        }
        this.countdownTimeoutIndex = setTimeout(() => {
          this.filterBanksMulti();
        }, 1000);
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data) {
      this.filteredData.next(this.data.slice());
      if (this.defaultAll && this.data.length > 0) {
        this.model = (['All'].concat(this.data));
      }
    }
    if (changes.values) {
      if (this.values.length === this.data.length && this.data.length > 0) {
        this.model = (['All'].concat(this.data));
      } else {
        this.model = this.values;
      }
    }
    if (changes.reset && this.reset) {
      if (this.resetValue && this.resetValue.length > 0) {
        this.model = this.resetValue.length === this.data.length ? (['All'].concat(this.data)) : this.resetValue;
        this.values = this.model;
      } else {
        this.model = [];
        this.values = [];
      }
      this.defaultAll = false;
    }
  }

  ngAfterViewInit() {
    if(this.multiple){
      Object.defineProperty(this.multiple, 'empty', {
        get: ()=> !this.model || this.model.length === 0,
        configurable: true
      });
    }
    this.setInitialValue();
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  optionChanges(option) {
    if (this.allSelected.selected) {
      this.allSelected.deselect();
      this.model.splice(0, 1);
    }
    if (this.model.findIndex(item => item.value === option.value.value) === -1) {
      this.model.push(option.value);
    } else {
      const index = this.model.findIndex(item => item.value === option.value.value);
      this.model.splice(index, 1);
    }
    if (this.model.length === this.data.length) {
      this.model.unshift('All');
      this.allSelected.select();
    }
  }

  optionAllChanges() {
    // if (this.matSelectSearchCtrl.value) {
    //   this.matSelectSearchCtrl.setValue('');
    // }
    if (this.allSelected.selected) {
      this.model = ['All'].concat(this.data.slice());
    } else {
      this.model = [];
      this.allSelected.deselect();
    }
  }

  public getValue(item) {
    return item;
  }

  scrolledIndexChange($event) {
    if (!this.initialRange) {
      this.initialRange = this.cdkVirtualScrollViewport.getRenderedRange();
    }

  }

  openedChange(opened: boolean) {
    if (!opened) {
      this.result.emit(this.model);
      this.cdkVirtualScrollViewport.setRenderedContentOffset(0);
      this.cdkVirtualScrollViewport.setRenderedRange(this.initialRange);
      (this.multiple as any).empty = !this.model || this.model.length === 0;
    }
  }

  filterBanksMulti() {
    if (!this.data) {
      return;
    }
    // get the search keyword
    let search = this.matSelectSearchCtrl.value;
    if (!search) {
      this.filteredData.next(this.data.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the banks
    this.filteredData.next(
      this.data.filter(item => item.name.toLowerCase().indexOf(search) > -1)
    );
  }

  setInitialValue() {
    this.filteredData
      .pipe(take(1), takeUntil(this.onDestroy))
      .subscribe(() => {
        // setting the compareWith property to a comparison function
        // triggers initializing the selection according to the initial value of
        // the form control (i.e. _initializeSelection())
        // this needs to be done after the filteredBanks are loaded initially
        // and after the mat-option elements are available
        this.multiple.compareWith = (a: any, b: any) => a && b && a.name === b.name;
      });
  }

  isSelect(option: MatOption) {
    if (this.model.findIndex(item => item.value === option.value.value) !== -1) {
      option.select();
    } else {
      option.deselect();
    }
  }

  getNames() {
    return this.model.map(x => x.name).toString();
  }

  selectAll(option: MatOption) {
    if (this.model[0] === 'All') {
      option.select();
    } else {
      option.deselect();
    }
  }

}
